import { Logger } from "../../../02_utils/logger";
import { Utils } from "../../../02_utils/utils";

export class PanelService {
  constructor (private $log: Logger, private $utils: Utils) {
    document.addEventListener('globalPanel:open', event => {
      this.openGlobalPanel();
    });
    document.addEventListener('globalPanel:close', event => {
      this.closeGlobalPanel();
    });
  }

  /**
   * Selects the global panel element from the DOM.
   * @returns the panel, or null.
   */
  public getGlobalPanel(): HTMLElement | null {
    const globalPanel: HTMLElement = document.querySelector('.l-global-panel');
    return globalPanel ?? null;
  }

  /**
   * Gets all buttons that should open the panel from the DOM.
   * @returns a NodeListOf HTMLElements or null, if nothing found.
   */
  public getAllPanelButtons(): NodeListOf<HTMLLinkElement> | null {
    const panelButtons: NodeListOf<HTMLLinkElement> = document.querySelectorAll('.e-panel-button');
    return panelButtons ?? null;
  }

  /**
   * Sets the content into the panel.
   * @param headline a headline string
   * @param url the url from the predefined page
   * @returns true if the panel is set. Otherwise false
   */
  public async setContentToPanel(headline: string | null | undefined, url: string): Promise<boolean> {
    const panel: HTMLElement = this.getGlobalPanel();

    if (!panel) { return false; }

    const headlineElement: HTMLElement = panel.querySelector('.l-global-panel__headline');

    if (headlineElement && headline) {
      const headlineH4: HTMLElement = headlineElement.querySelector('h4');
      if (headlineH4) {
        headlineH4.innerHTML = headline;
      }
    }

    const data = await this.requestContent(url);

    if (data) {
      const panelContent: HTMLElement = panel.querySelector('.l-global-panel__content');

      if (panelContent) {
        panelContent.innerHTML = data;
        return true;
      } else {
        return false;
      }
    }

    return false;
  }

  /**
   * Function for opening the global panel
   * This gets called via an event listener
   */
  private openGlobalPanel(): void {
    const body: HTMLBodyElement = document.querySelector('body');
    const panel = this.getGlobalPanel();

    if (body && panel) {
      this.$utils.addOrRemoveClases('add', 'open', body, panel);
      this.$utils.addOrRemoveClases('add', 'js-panel-backdrop-element', panel);
      this.$utils.activateFlyoutPanelBackdrop();
    }

  }

  /**
   * Function for closing the global panel
   * This gets called via an event listener
   * Also deactivates content on close
   */
  private closeGlobalPanel(): void {
    const body: HTMLBodyElement = document.querySelector('body');
    const panel = this.getGlobalPanel();

    if (body && panel) {
      this.$utils.addOrRemoveClases('remove', 'open', body, panel);
      this.$utils.addOrRemoveClases('remove', 'js-panel-backdrop-element', panel);
      this.$utils.deactivateFlyoutPanelBackdrop();
      const panelBody: HTMLElement = panel.querySelector('l-global-panel__content');
      if (panelBody) {
        panelBody.innerHTML = '';
      }
    }
  }

  /**
   * Requests the content from the url given
   * @param url the url string
   * @returns a html string, or false if not found
   */
  private async requestContent(url: string): Promise<string | false> {
    const requestUrl = `${url}`;
    const request = new Request(requestUrl, {
      method: 'GET',
      headers: { 'content-type': 'text/html' }
    });

    const data: string | false = await fetch(request).then((response) => {
        if (response.status == 200) {
          return response.text();
        } else {
          throw new Error(response.statusText);
        }
      }).then((response) => {
        this.$log.debug('PanelService response:', response, url);
        return response;
      }).catch((error) => {
        this.$log.error('PanelService error:', error, url);
        return false;
      });

    return data;
  }
}
